import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Users, Search, ChevronLeft, ChevronRight,
  AlertCircle, User, Mail, Phone, Calendar,
  FileText, DollarSign, RefreshCw, Eye
} from 'lucide-react';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Client List Component
const ClientList = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 20,
    total: 0
  });

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('skip', pagination.skip);
      params.append('limit', pagination.limit);

      const response = await fetch(`${API_URL}/api/admin/clients?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied');
        }
        throw new Error('Failed to fetch clients');
      }

      const data = await response.json();
      setClients(data.clients || []);
      setPagination(prev => ({ ...prev, total: data.total || 0 }));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, search, pagination.skip, pagination.limit]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSearch = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const handlePageChange = (direction) => {
    setPagination(prev => ({
      ...prev,
      skip: direction === 'next' 
        ? prev.skip + prev.limit 
        : Math.max(0, prev.skip - prev.limit)
    }));
  };

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <AlertCircle size={48} />
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/admin')} className="admin-btn primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <button onClick={() => navigate('/admin')} className="back-btn">
              <ChevronLeft size={20} />
              Back
            </button>
            <h1>Client Management</h1>
            <p className="admin-subtitle">
              View and manage registered patients
            </p>
          </div>
        </div>

      {/* Search */}
      <div className="admin-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            data-testid="client-search-input"
          />
        </div>
        <button onClick={fetchClients} className="admin-btn secondary">
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Clients Table */}
      {loading ? (
        <div className="admin-loading">
          <RefreshCw className="spin" size={32} />
          <p>Loading clients...</p>
        </div>
      ) : clients.length === 0 ? (
        <div className="admin-empty">
          <Users size={48} />
          <h3>No clients found</h3>
          <p>No clients match your search criteria</p>
        </div>
      ) : (
        <>
          <div className="admin-table-container">
            <table className="admin-table" data-testid="clients-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Cases</th>
                  <th>Registered</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id} data-testid={`client-row-${client.id}`}>
                    <td>
                      <div className="client-cell">
                        <div className="client-avatar">
                          <User size={18} />
                        </div>
                        <span className="client-name">
                          {client.first_name} {client.last_name}
                        </span>
                      </div>
                    </td>
                    <td>{client.email}</td>
                    <td>{client.phone || '-'}</td>
                    <td>
                      <span className="case-count-badge">
                        {client.case_count || 0} cases
                      </span>
                    </td>
                    <td>{formatDate(client.created_at)}</td>
                    <td>
                      <button
                        onClick={() => navigate(`/admin/clients/${client.id}`)}
                        className="action-icon-btn"
                        title="View Details"
                        data-testid={`view-client-${client.id}`}
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="admin-pagination">
            <span className="pagination-info">
              Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
            </span>
            <div className="pagination-buttons">
              <button
                onClick={() => handlePageChange('prev')}
                disabled={pagination.skip === 0}
                className="pagination-btn"
              >
                <ChevronLeft size={18} />
                Previous
              </button>
              <button
                onClick={() => handlePageChange('next')}
                disabled={pagination.skip + pagination.limit >= pagination.total}
                className="pagination-btn"
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
};

// Client Detail Component
const ClientDetail = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchClientDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/clients/${clientId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch client details');
      }

      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, clientId]);

  useEffect(() => {
    fetchClientDetail();
  }, [fetchClientDetail]);

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-loading">
          <RefreshCw className="spin" size={32} />
          <p>Loading client details...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <AlertCircle size={48} />
          <h2>Error</h2>
          <p>{error || 'Client not found'}</p>
          <button onClick={() => navigate('/admin/clients')} className="admin-btn primary">
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  const { client, cases, intakes, payments } = data;

  return (
    <div className="admin-page-wrapper">
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <button onClick={() => navigate('/admin/clients')} className="back-btn">
              <ChevronLeft size={20} />
              Back to Clients
            </button>
            <h1>Client Profile</h1>
            <p className="admin-subtitle">
              {client?.first_name} {client?.last_name}
            </p>
          </div>
        </div>

      {/* Client Info Card */}
      <div className="intake-detail-header">
        <div className="patient-info-card">
          <div className="patient-avatar large">
            <User size={40} />
          </div>
          <div className="patient-details">
            <h2>{client?.first_name} {client?.last_name}</h2>
            <div className="patient-meta">
              <span><Mail size={14} /> {client?.email}</span>
              {client?.phone && <span><Phone size={14} /> {client.phone}</span>}
              <span><Calendar size={14} /> Member since {formatDate(client?.created_at)}</span>
            </div>
          </div>
          <div className="client-stats">
            <div className="client-stat">
              <span className="stat-number">{cases?.length || 0}</span>
              <span className="stat-label">Cases</span>
            </div>
            <div className="client-stat">
              <span className="stat-number">{intakes?.length || 0}</span>
              <span className="stat-label">Intakes</span>
            </div>
            <div className="client-stat">
              <span className="stat-number">${calculateTotalSpent(payments)}</span>
              <span className="stat-label">Total Spent</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'cases' ? 'active' : ''}`}
          onClick={() => setActiveTab('cases')}
        >
          Cases ({cases?.length || 0})
        </button>
        <button 
          className={`tab ${activeTab === 'intakes' ? 'active' : ''}`}
          onClick={() => setActiveTab('intakes')}
        >
          Intakes ({intakes?.length || 0})
        </button>
        <button 
          className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          Payments ({payments?.length || 0})
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-grid">
            <div className="overview-card">
              <h4>Contact Information</h4>
              <dl>
                <dt>Email</dt>
                <dd>{client?.email}</dd>
                <dt>Phone</dt>
                <dd>{client?.phone || 'Not provided'}</dd>
                <dt>Account Created</dt>
                <dd>{formatDateTime(client?.created_at)}</dd>
              </dl>
            </div>

            <div className="overview-card">
              <h4>Recent Activity</h4>
              {cases && cases.length > 0 ? (
                <div className="recent-activity-list">
                  {cases.slice(0, 3).map((caseItem) => (
                    <div key={caseItem.id} className="activity-item">
                      <FileText size={16} />
                      <span>Case #{caseItem.case_number}</span>
                      <span className={`status-badge small ${caseItem.status}`}>
                        {caseItem.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-activity">No recent activity</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'cases' && (
          <div className="list-section">
            {cases && cases.length > 0 ? (
              <div className="cases-list">
                {cases.map((caseItem) => (
                  <div key={caseItem.id} className="list-item-card">
                    <div className="list-item-header">
                      <span className="list-item-title">
                        <FileText size={18} />
                        Case #{caseItem.case_number}
                      </span>
                      <span className={`status-badge ${caseItem.status}`}>
                        {caseItem.status}
                      </span>
                    </div>
                    <div className="list-item-meta">
                      <span>Service: {caseItem.service_type}</span>
                      <span>Created: {formatDate(caseItem.created_at)}</span>
                    </div>
                    {caseItem.chief_complaint && (
                      <p className="list-item-description">{caseItem.chief_complaint}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-empty small">
                <FileText size={32} />
                <p>No cases found</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'intakes' && (
          <div className="list-section">
            {intakes && intakes.length > 0 ? (
              <div className="intakes-list">
                {intakes.map((intake) => (
                  <div 
                    key={intake.id} 
                    className="list-item-card clickable"
                    onClick={() => navigate(`/admin/intakes/${intake.id}`)}
                  >
                    <div className="list-item-header">
                      <span className="list-item-title">
                        {formatPackage(intake.selected_package)}
                      </span>
                      <span className={`status-badge ${intake.status}`}>
                        {intake.status}
                      </span>
                    </div>
                    <div className="list-item-meta">
                      <span>${intake.amount?.toLocaleString() || '0'}</span>
                      <span>{formatDate(intake.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-empty small">
                <FileText size={32} />
                <p>No intakes found</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="list-section">
            {payments && payments.length > 0 ? (
              <div className="payments-list">
                {payments.map((payment) => (
                  <div key={payment.id} className="list-item-card">
                    <div className="list-item-header">
                      <span className="list-item-title">
                        <DollarSign size={18} />
                        ${payment.amount?.toLocaleString() || '0'}
                      </span>
                      <span className={`status-badge ${payment.payment_status}`}>
                        {payment.payment_status}
                      </span>
                    </div>
                    <div className="list-item-meta">
                      <span>{payment.service_type || 'Service'}</span>
                      <span>{formatDate(payment.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-empty small">
                <DollarSign size={32} />
                <p>No payments found</p>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

// Helper functions
const formatPackage = (pkg) => {
  const packages = {
    'diagnostic-review': 'Diagnostic Review',
    'advocacy-launch': 'Advocacy Launch',
    'deep-dive': 'Deep Dive'
  };
  return packages[pkg] || pkg || 'Unknown';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const calculateTotalSpent = (payments) => {
  if (!payments || payments.length === 0) return '0';
  const total = payments
    .filter(p => p.payment_status === 'paid')
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  return total.toLocaleString();
};

export { ClientList, ClientDetail };
